import React, { useState } from "react"
import PropTypes from "prop-types"
import mount from "core/mount"
import useFetch from "hooks/use-new-fetch"
import { toast } from "@kit/design/toast"

const ApprovalBanner = ({
  salesBlocked,
  hasPendingRequest,
  blocklistCount,
  manual,
  requestApprovalUrl
}) => {
  const getInitialMessage = () => {
    if (hasPendingRequest) {
      return (
        <>
          Some features of your account are disabled. Continue the conversation
          with customer support by clicking here.
        </>
      )
    }
    return (
      <>
        Some features of your account are disabled until it has been approved by
        our team. Start the approvals process by clicking here.
      </>
    )
  }

  const [message, setMessage] = useState(getInitialMessage())
  const [clickedBanner, setClickedBanner] = useState(false)
  const [intercomAvailable, setIntercomAvailable] = useState(true)

  const [approvalRequest] = useFetch(requestApprovalUrl)

  const requestApproval = () => {
    try {
      approvalRequest.put()
    } catch (e) {
      toast({
        type: "error",
        text: "Error requesting approval. Contact customer support by emailing help@kit.com."
      })
    }
  }

  const handleClick = () => {
    /* eslint-disable-next-line no-undef */
    if (typeof Intercom === "undefined" || !Intercom) {
      setIntercomAvailable(false)
      return
    }

    /* eslint-disable-next-line no-undef */
    const intercom = Intercom

    // The Intercom approval bot is configured to respond to this click (based on the button id).
    // Additional attributes that can be used in the bot.
    intercom("update", {
      commerce_needs_review: salesBlocked,
      manual_blocklist: manual,
      blocklist_count: blocklistCount
    })

    requestApproval()

    if (hasPendingRequest || clickedBanner) {
      // A conversation was previously started. Just open the chat window so it can be continued.
      intercom("show")
    } else {
      // We expect that the Intercom bot has triggered and the chat window is now open at the first question.
      setMessage("Please complete the questions in the lower-right corner.")
    }

    setClickedBanner(true)
  }

  // Set the button id appropriately. The bot in Intercom is watching for the "approval-trigger" id.
  // Don't trigger the bot again if the user already has a pending request or has clicked the banner
  // since the last page refresh.
  const buttonId =
    hasPendingRequest || clickedBanner ? "approval-button" : "approval-trigger"

  return (
    <React.Fragment>
      {intercomAvailable && (
        <button
          id={buttonId}
          className="alert-no-hide alert-warning alert--banner alert--account-review"
          style={{ textDecoration: "none" }}
          onClick={handleClick}
        >
          {message}
        </button>
      )}

      {!intercomAvailable && (
        <span>
          Uh oh! The browser blocked our chat app. Please email{" "}
          <a href="mailto:help@kit.com">help@kit.com</a> to start the approvals
          process.
        </span>
      )}
    </React.Fragment>
  )
}

ApprovalBanner.propTypes = {
  blocklistCount: PropTypes.number.isRequired,
  hasPendingRequest: PropTypes.bool.isRequired,
  salesBlocked: PropTypes.bool.isRequired,
  requestApprovalUrl: PropTypes.string.isRequired,
  manual: PropTypes.bool
}

export { ApprovalBanner }

mount({ "Account.ApprovalBanner": ApprovalBanner })
